import LoadingButton from "@mui/lab/LoadingButton";
import * as Yup from "yup";
import { useFormik } from "formik";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { API_URL } from "../../utilities";
import { useAuth } from "../../context/auth";
import { useNotifications } from "@toolpad/core/useNotifications";
import React, { useEffect } from "react";
import FollowUpGroupForm from "./components/followup-group-form";
import { getRoutes } from "src/utilities/routes";

const validationSchema = Yup.object({
  serviceRef: Yup.string().required("این فیلد الزامی است"),
  priority: Yup.string().required("این فیلد الزامی است"),
  title: Yup.string().required("این فیلد الزامی است"),
});

const initialValues = {
  serviceRef: 0,
  priority: 0,
  title: "",
  isSendSMS: true,
  sampleSMSPayment: "",
};

interface NewFollowUpGroupProps {
  open: boolean;
  handleClose: () => void;
  refetch: () => void;
  editingData?: any; // داده‌های ویرایش (اختیاری)
}

const NewFollowUpGroup = ({
  open,
  handleClose,
  refetch,
  editingData,
}: NewFollowUpGroupProps) => {
  const { user } = useAuth();
  const Routes = getRoutes();
  const isEditMode = !!editingData;

  const { mutateAsync: insertAsync, isPending: isInsertPending } = useMutation({
    mutationFn: async (body: any) => {
      const response = await fetch(Routes.FollowupRoutes, {
        method: "POST",
        headers: {
          Accept: "*/*",
          "Content-Type": "application/json-patch+json",
          Authorization: `Bearer ${user?.token}`,
        },
        body: JSON.stringify(body),
      });
      return await response.json();
    },
    onSuccess: (responseData) => {
      JSON.stringify(responseData);
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const { mutateAsync: updateAsync, isPending: isUpdatePending } = useMutation({
    mutationFn: async (body: any) => {
      const response = await fetch(Routes.FollowupRoutes + body.id, {
        method: "PUT",
        headers: {
          Accept: "*/*",
          "Content-Type": "application/json-patch+json",
          Authorization: `Bearer ${user?.token}`,
        },
        body: JSON.stringify(body),
      });
      return await response.json();
    },
    onSuccess: (responseData) => {
      JSON.stringify(responseData);
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const notifications = useNotifications();
  const isPending = isInsertPending || isUpdatePending;

  const formik = useFormik({
    initialValues, // مقادیر اولیه فرم
    validationSchema, // schema اعتبارسنجی
    enableReinitialize: true, // اجازه به‌روزرسانی initialValues
    onSubmit: async (values) => {
      let res;
      if (isEditMode) {
        // ویرایش
        res = await updateAsync({ ...values, id: editingData.id });
        if (res.status === true) {
          notifications.show("گروه پیگیری با موفقیت ویرایش شد!", {
            key: "edit-task",
            severity: "success",
            autoHideDuration: 5000,
          });
        } else {
          notifications.show("خطا در ویرایش!", {
            key: "err-edit-task",
            severity: "error",
            autoHideDuration: 5000,
          });
        }
      } else {
        // ایجاد
        res = await insertAsync(values);
        if (res.status === true) {
          notifications.show("گروه پیگیری با موفقیت ثبت شد!", {
            key: "new-task",
            severity: "success",
            autoHideDuration: 5000,
          });
        } else {
          notifications.show("خطا در ذخیره سازی!", {
            key: "err-task",
            severity: "error",
            autoHideDuration: 5000,
          });
        }
      }

      if (res.status === true) {
        refetch();
        handleClose();
        formik.resetForm();
      }
    },
  });

  // پر کردن فرم با داده‌های ویرایش
  useEffect(() => {
    if (open && editingData) {
      formik.setValues({
        serviceRef: editingData.serviceRef || 0,
        priority: editingData.priority || 0,
        title: editingData.title || "",
        isSendSMS: editingData.isSendSMS ?? true,
        sampleSMSPayment: editingData.sampleSMSPayment || "",
      });
    } else if (open && !editingData) {
      // ریست کردن فرم برای حالت ایجاد
      formik.resetForm();
    }
  }, [open, editingData]);

  return (
    <React.Fragment>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xl"
        PaperProps={{
          component: "form",
          onSubmit: formik.handleSubmit,
          sx: {
            width: "100%",
          },
        }}
      >
        <DialogTitle>
          {isEditMode ? "ویرایش گروه پیگیری" : "گروه پیگیری جدید"}
        </DialogTitle>
        <DialogContent>
          <FollowUpGroupForm formik={formik} />
        </DialogContent>
        <DialogActions>
          <LoadingButton
            loading={isPending}
            variant="contained"
            color="success"
            size="medium"
            fullWidth
            type="submit"
          >
            {isEditMode ? "ویرایش" : "ذخیره"}
          </LoadingButton>
          <Button color="error" variant="contained" onClick={handleClose}>
            انصراف
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

export default NewFollowUpGroup;
